import { Component, OnInit, AfterContentChecked, Inject, ApplicationRef, NgZone } from '@angular/core';
import { Router, RouteConfigLoadEnd, RouteConfigLoadStart, Event as RouterEvent, NavigationStart, NavigationEnd } from "@angular/router";

import { MatDialogRef } from '@angular/material/dialog';
import { MatDialog } from '@angular/material';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';

import { SessionStorageService } from "./shared/services/session-storage.service";
import { SharedService } from './shared/services/shared.service';
import { RestrictOutsideScrollService } from './shared/services/restrict-outside-scroll.service';

import { ContractSignedComponent } from './shared/components/contract-signed/contract-signed.component';

import { config } from '../../config/index';

@Component({
	selector: 'my-app',
	template: require('./practice-app.component.html'),
	styles: [require('./practice-app.component.less')],
	preserveWhitespaces: true
})
export class PracticeAppComponent implements OnInit, AfterContentChecked {

	subscriptionDetails: any = [];
	favIcon: any;
	isShowingRouteLoadIndicator: boolean;
	isTouchDevice: boolean;
	// in activity logout
	logoutCountdown: any = null;
	isLoginPage: boolean = true;

	dialogRefContractPopup: MatDialogRef<ContractSignedComponent>;

	constructor(
		private _sessionStorageService: SessionStorageService,
		private _sharedService: SharedService,
		private router: Router,
		@Inject('utilService') private utilService: any,
		public dialog: MatDialog,
		private _restrictOutsideScrollService: RestrictOutsideScrollService,
		private idle: Idle,
		private appRef: ApplicationRef,
		private zone: NgZone
	) {
		this.isShowingRouteLoadIndicator = false;
		// As the router loads modules asynchronously (via loadChildren), we're going to
		// keep track of how many asynchronous requests are currently active. If there is
		// at least one pending load request, we'll show the indicator.
		var asyncLoadCount = 0;

		// The Router emits special events for "loadChildren" configuration loading. We
		// just need to listen for the Start and End events in order to determine if we
		// have any pending configuration requests.
		router.events.subscribe(
			(event: RouterEvent): void => {

				if (event instanceof RouteConfigLoadStart) {
					asyncLoadCount++;
				} else if (event instanceof RouteConfigLoadEnd) {
					asyncLoadCount--;
				}

				// If there is at least one pending asynchronous config load request,
				// then let's show the loading indicator.
				// --
				// CAUTION: I'm using CSS to include a small delay such that this loading
				// indicator won't be seen by people with sufficiently fast connections.
				this.isShowingRouteLoadIndicator = !!asyncLoadCount;

				// reintialize ngIdle timeout on page reload
				if (event instanceof NavigationStart) {
					if (!router.navigated) {
						this.resetTimeout();
					}
				}

				if (event instanceof NavigationEnd) {
					if (event.url.includes('login') || event.url.includes('logout')) {
						this.isLoginPage = true;
					}
				}
			}
		);

		// create contract signed event listener if role is COO/LM
		this._sessionStorageService.readItem(config.role).then((data: any) => {
			if (data?.role === 'Location Manager' || data?.role === 'COO Admin') {
				this.utilService.contractSignedPopup.subscribe(this.showContractSignedPopup);
			}
		});

		// sets an idle timeout of 19 mins (1140s).
		idle.setIdle(1140);
		// sets a timeout period of 60 seconds. after total 20 mins of inactivity, the user will be considered timed out.
		idle.setTimeout(60);
		// sets the default interrupts, in this case, things like clicks, scrolls, touches, mouse moves to the document
		idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

		idle.onIdleEnd.subscribe(() => {
			this.resetTimeout();
			this.appRef.tick();
		});

		idle.onTimeout.subscribe(() => {
			this.logoutCountdown = null;
			this.zone.run(() => this.router.navigate(['/logout']));
		});

		idle.onTimeoutWarning.subscribe((countdown: any) => {
			this.logoutCountdown = countdown;
		});

		this.utilService.getUserLoggedIn().subscribe((userLoggedIn: any) => {
			this.logoutCountdown = null;
			this.isLoginPage = !userLoggedIn;
			if (userLoggedIn) {
				idle.watch();
			} else {
				idle.stop();
			}
		});
	}

	resetTimeout() {
		this.logoutCountdown = null;
		if (sessionStorage.getItem('logined')) {
			this.isLoginPage = false;
			this.idle.watch();
		} else {
			this.isLoginPage = true;
		}
	}

	showContractSignedPopup = (eventData: any) => {
		if (eventData.signingComplete) {
			this._restrictOutsideScrollService.hideBackgroundScroll();
			this.dialogRefContractPopup = this.dialog.open(ContractSignedComponent, {
				disableClose: true,
				data: {
					id: eventData.id
				},
			});

			this.dialogRefContractPopup.afterClosed().subscribe(result => {
				this._restrictOutsideScrollService.showBackgroundScroll();
			});
		}
	}

	onDeactivate() {
		document.body.scrollTop = 0;
	}

	ngOnInit() {
		if (!sessionStorage.getItem('logined')) {
			this._sessionStorageService.clearItems();
		}
		this.favIcon = require('../assets/images/favicon.ico');
		if (window.document.getElementById('appFavicon')) {
			window.document.getElementById('appFavicon').setAttribute('href', this.favIcon);
		}
		this._sharedService.setTranslateLanguage();
	}

	ngAfterContentChecked() {
		this.isTouchDevice = ("ontouchstart" in document.documentElement);
	}
}
